/**
 * Helpery pomocnicze dla modulu Grafik Pracy
 */

const STATUS_COLORS = {
  planowana: 'var(--info)',
  zatwierdzona: 'var(--success)',
  urlop: 'var(--orange)',
  chorobowe: 'var(--warning)',
  nieobecnosc: 'var(--danger)',
  wolne: 'var(--text-muted)'
};

const STATUS_LABELS = {
  planowana: 'Planowana',
  zatwierdzona: 'Zatwierdzona',
  urlop: 'Urlop',
  chorobowe: 'Chorobowe',
  nieobecnosc: 'Nieobecnosc',
  wolne: 'Wolne'
};

const WEEKDAYS_SHORT = ['Nd', 'Pn', 'Wt', 'Sr', 'Cz', 'Pt', 'So'];
const WEEKDAYS_FULL = ['Niedziela', 'Poniedzialek', 'Wtorek', 'Sroda', 'Czwartek', 'Piatek', 'Sobota'];

const MONTHS_PL = [
  'Styczen', 'Luty', 'Marzec', 'Kwiecien', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpien', 'Wrzesien', 'Pazdziernik', 'Listopad', 'Grudzien'
];

/**
 * Format godzin dziesietnych w polskim formacie z "h"
 * 8 -> "8,00 h"
 * 8.5 -> "8,50 h"
 */
export const formatHours = (decimal) => {
  if (decimal === null || decimal === undefined || isNaN(decimal)) return '0,00 h';
  const num = Number(decimal);
  return `${num.toFixed(2).replace('.', ',')} h`;
};

/**
 * Konwersja "8.00" -> "8,00" (PL)
 */
export const pl = (n) => {
  if (n === null || n === undefined) return '0,00';
  const s = typeof n === 'number' ? n.toFixed(2) : String(n);
  return s.replace('.', ',');
};

/**
 * Kolor wg statusu zmiany
 */
export const getStatusColor = (status) => {
  return STATUS_COLORS[status] || STATUS_COLORS.planowana;
};

/**
 * Polska etykieta statusu
 */
export const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status || '-';
};

/**
 * Wszystkie statusy do dropdownow
 */
export const getAllStatuses = () => Object.keys(STATUS_LABELS).map(value => ({
  value,
  label: STATUS_LABELS[value]
}));

/**
 * Tablica obiektow Date dla calego miesiaca (1..N)
 */
export const getMonthDays = (year, month /* 1-12 */) => {
  const m = Number(month);
  const y = Number(year);
  const last = new Date(y, m, 0).getDate();
  const days = [];
  for (let d = 1; d <= last; d++) {
    days.push(new Date(y, m - 1, d));
  }
  return days;
};

/**
 * Skrot dnia tygodnia (Pn, Wt, ...) - week start poniedzialek
 */
export const getWeekday = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return WEEKDAYS_SHORT[d.getDay()] || '';
};

/**
 * Pelna nazwa dnia tygodnia
 */
export const getWeekdayFull = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return WEEKDAYS_FULL[d.getDay()] || '';
};

/**
 * Indeks dnia w tygodniu (0=poniedzialek..6=niedziela)
 */
export const getMondayBasedIndex = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const js = d.getDay(); // 0=ndz
  return js === 0 ? 6 : js - 1;
};

/**
 * "08:00:00" -> "08:00"
 */
export const parseShiftTime = (timeStr) => {
  if (!timeStr) return '';
  const s = String(timeStr);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  return s;
};

/**
 * Zwraca ISO date string YYYY-MM-DD
 */
export const toISODate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Polska nazwa miesiaca + rok
 */
export const formatMonthYear = (year, month /* 1-12 */) => {
  const m = Number(month);
  if (m < 1 || m > 12) return `${year}`;
  return `${MONTHS_PL[m - 1]} ${year}`;
};

/**
 * Lista miesiecy do dropdownu
 */
export const getMonthsList = () => MONTHS_PL.map((label, idx) => ({
  value: idx + 1,
  label
}));

/**
 * Oblicz godziny zmiany na podstawie godzina_od, godzina_do, przerwa_minut
 * Zwraca number (np. 8 lub 7.5)
 */
export const calculateShiftHours = (godzinaOd, godzinaDo, przerwaMinut = 0) => {
  if (!godzinaOd || !godzinaDo) return 0;
  const [h1, m1] = parseShiftTime(godzinaOd).split(':').map(Number);
  const [h2, m2] = parseShiftTime(godzinaDo).split(':').map(Number);
  if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return 0;
  let minutes = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (minutes < 0) minutes += 24 * 60; // przejscie przez polnoc
  minutes -= Number(przerwaMinut || 0);
  if (minutes < 0) minutes = 0;
  return Number((minutes / 60).toFixed(2));
};

/**
 * Krotki kod lokalizacji (3 znaki) z nazwy.
 * np. "SFD Leszno" -> "LES"
 */
export const shortLocationCode = (name) => {
  if (!name) return '---';
  const cleaned = String(name).trim();
  // Wez ostatni segment po ostatniej spacji
  const parts = cleaned.split(/\s+/);
  const word = parts[parts.length - 1] || cleaned;
  return word.slice(0, 3).toUpperCase();
};

/**
 * Mapa: ISO date -> array of shifts
 */
export const groupShiftsByDate = (shifts = []) => {
  const map = {};
  shifts.forEach(s => {
    const key = s.data || s.date;
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push(s);
  });
  return map;
};

const helpers = {
  formatHours,
  pl,
  getStatusColor,
  getStatusLabel,
  getAllStatuses,
  getMonthDays,
  getWeekday,
  getWeekdayFull,
  getMondayBasedIndex,
  parseShiftTime,
  toISODate,
  formatMonthYear,
  getMonthsList,
  calculateShiftHours,
  shortLocationCode,
  groupShiftsByDate
};

export default helpers;
